// AudioWorklet that turns a MediaStream into 16 kHz mono s16le frames (TT-1102).
//
// Runs on the audio thread, so it keeps flowing when the tab is in the
// background — Transcribe drops a session that goes 15 s without audio, so
// a muted capture still ships frames, just silent ones. Shipped as a Blob
// URL so the bundle owns it; CRA has no worklet loader and a public/ asset
// would tie it to the deploy path.
//
// Messages to the main thread:
//   { type: "pcm", frame: ArrayBuffer }   ~100 ms of s16le at TARGET_RATE
//   { type: "level", rms: number }         once per frame, for the meter

export const TARGET_SAMPLE_RATE = 16000;

const WORKLET_SOURCE = `
const TARGET_RATE = ${TARGET_SAMPLE_RATE};
const FRAME_SAMPLES = Math.round(TARGET_RATE / 10); // 100 ms

class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.muted = false;
    this.ratio = sampleRate / TARGET_RATE;
    this.pending = new Float32Array(0);
    // fractional read position carried across quanta — dropping it made the
    // 44.1 kHz media clock run 0.5% fast
    this.phase = 0;
    this.out = new Int16Array(FRAME_SAMPLES);
    this.outLen = 0;
    this.sumSq = 0;
    this.port.onmessage = (e) => {
      if (e.data && e.data.type === "mute") this.muted = !!e.data.muted;
    };
  }

  // Down-mix to mono, then resample by averaging each ratio-wide window —
  // a box filter is plenty for speech at 3:1, and it is cheap.
  process(inputs) {
    const input = inputs[0];
    if (!input || !input.length) return true;
    const channels = input.length;
    const n = input[0].length;
    const mono = new Float32Array(this.pending.length + n);
    mono.set(this.pending, 0);
    for (let i = 0; i < n; i += 1) {
      let s = 0;
      for (let c = 0; c < channels; c += 1) s += input[c][i];
      mono[this.pending.length + i] = s / channels;
    }
    let pos = this.phase;
    while (pos + this.ratio <= mono.length) {
      const end = pos + this.ratio;
      let acc = 0;
      let count = 0;
      for (let j = Math.floor(pos); j < end; j += 1) {
        acc += mono[j];
        count += 1;
      }
      let v = count ? acc / count : 0;
      if (this.muted) v = 0;
      this.sumSq += v * v;
      const clamped = Math.max(-1, Math.min(1, v));
      this.out[this.outLen] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      this.outLen += 1;
      if (this.outLen === FRAME_SAMPLES) this.flush();
      pos = end;
    }
    const consumed = Math.floor(pos);
    this.pending = mono.subarray(consumed);
    this.phase = pos - consumed;
    return true;
  }

  flush() {
    const frame = this.out.slice(0, this.outLen);
    this.port.postMessage({ type: "pcm", frame: frame.buffer }, [frame.buffer]);
    this.port.postMessage({ type: "level", rms: Math.sqrt(this.sumSq / this.outLen) });
    this.outLen = 0;
    this.sumSq = 0;
  }
}

registerProcessor("pcm-capture", PcmCaptureProcessor);
`;

let workletUrl = null;

export function pcmWorkletUrl() {
  if (!workletUrl) {
    workletUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "application/javascript" }));
  }
  return workletUrl;
}
